import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import { operationalWeekendLabels } from '../../shared/operationalWeek.ts'
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminDataTable,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  type AdminTableColumn,
} from './AdminUi'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
  idleExpiresAt: string
  absoluteExpiresAt: string
}

type StoreStatusSource =
  | 'emergency-pause'
  | 'manual-override'
  | 'scheduled-exception'
  | 'staging-qa-override'
  | 'weekly-schedule'

type DashboardOrder = {
  orderId: string
  customerName: string
  paymentMethod: 'card' | 'bank-transfer'
  paymentStatus: 'paid' | 'pending'
  totalCents: number
  createdAt: string
  deliveryDay: 'saturday' | 'sunday' | null
  suburb: string | null
  boxCount: number
}

type DashboardPayload = {
  generatedAt: string
  environment: string
  drop: {
    startsAt: string
    endsAt: string
    timeZone: 'Pacific/Auckland'
  }
  store: {
    ordersOpen: boolean
    realOrdersOpen: boolean
    qaOverrideActive: boolean
    schedule: string
    stateSource: StoreStatusSource
  }
  metrics: {
    ordersThisDrop: number
    paidOrdersThisDrop: number
    awaitingPaymentThisDrop: number
    paidRevenueThisDropCents: number
    customersThisDrop: number
    saturdayOrdersThisDrop: number
    sundayOrdersThisDrop: number
    lifetimeOrders: number
    lifetimePaidRevenueCents: number
  }
  attention: {
    payments: Array<{
      orderId: string
      customerName: string
      dueAt: string | null
      expired: boolean
      href: string
    }>
    fulfilment: {
      count: number
      orders: Array<{
        orderId: string
        customerName: string
        status: 'received' | 'preparing' | 'packed' | 'out_for_delivery' | null
        href: string
      }>
      href: string
    }
    zeroStockAddOns: Array<{
      id: string
      name: string
      stockPacks: number
      reservedPacks: number
      orderablePacks: number
      href: string
    }>
  }
  recentOrders: DashboardOrder[]
}

type SupportNotificationsPayload = {
  unreadNotifications: number
  notifications: Array<{
    id: string
    eventType: 'public_ticket' | 'customer_reply'
    reference: string
    customerName: string | null
    createdAt: string
  }>
}

type EmailAttentionPayload = {
  failedTransactional: number
  failedInternal: number
  failedTotal: number
}

const NZD = new Intl.NumberFormat('en-NZ', {
  style: 'currency',
  currency: 'NZD',
  minimumFractionDigits: 2,
})

const DATE_TIME = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
})

const DATE_ONLY = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
})

function formatMoney(cents: number) {
  return NZD.format(cents / 100)
}

function formatDateTime(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : DATE_TIME.format(date)
}

function formatDropRange(start: string, end: string) {
  const startDate = new Date(start)
  const endDate = new Date(new Date(end).getTime() - 1)
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 'Current drop'
  return `${DATE_ONLY.format(startDate)} – ${DATE_ONLY.format(endDate)}`
}

function deliveryLabel(day: DashboardOrder['deliveryDay']) {
  if (day === 'saturday') return 'Saturday'
  if (day === 'sunday') return 'Sunday'
  return 'Delivery day pending'
}

function paymentMethodLabel(method: DashboardOrder['paymentMethod']) {
  return method === 'bank-transfer' ? 'Bank transfer' : 'Card'
}

function storeStatusSourceLabel(source: StoreStatusSource) {
  if (source === 'emergency-pause') return 'Emergency pause'
  if (source === 'manual-override') return 'Temporary override'
  if (source === 'scheduled-exception') return 'Scheduled exception'
  if (source === 'staging-qa-override') return 'Staging QA override'
  return 'Weekly schedule'
}

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

const RECENT_ORDER_COLUMNS: AdminTableColumn<DashboardOrder>[] = [
  {
    key: 'order',
    label: 'Order',
    width: '20%',
    render: (row) => (
      <div className="admin-dashboard__order-primary">
        <strong>{row.orderId}</strong>
        <span>{formatDateTime(row.createdAt)}</span>
      </div>
    ),
  },
  {
    key: 'customer',
    label: 'Customer',
    width: '23%',
    render: (row) => (
      <div className="admin-dashboard__order-primary">
        <strong>{row.customerName}</strong>
        <span>{row.suburb || 'Suburb not recorded'}</span>
      </div>
    ),
  },
  {
    key: 'delivery',
    label: 'Delivery',
    render: (row) => (
      <div className="admin-dashboard__order-primary">
        <strong>{deliveryLabel(row.deliveryDay)}</strong>
        <span>{row.boxCount === 1 ? '1 box' : `${row.boxCount} boxes`}</span>
      </div>
    ),
  },
  {
    key: 'payment',
    label: 'Payment',
    render: (row) => (
      <div className="admin-dashboard__payment-cell">
        <AdminStatusPill tone={row.paymentStatus === 'paid' ? 'success' : 'warning'}>
          {row.paymentStatus === 'paid' ? 'Paid' : 'Awaiting payment'}
        </AdminStatusPill>
        <span>{paymentMethodLabel(row.paymentMethod)}</span>
      </div>
    ),
  },
  {
    key: 'total',
    label: 'Total',
    align: 'right',
    render: (row) => <strong className="admin-dashboard__order-total">{formatMoney(row.totalCents)}</strong>,
  },
]

export default function AdminDashboardPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null)
  const [supportNotifications, setSupportNotifications] = useState<SupportNotificationsPayload | null>(null)
  const [emailAttention, setEmailAttention] = useState<EmailAttentionPayload | null>(null)
  const [error, setError] = useState('')
  const [attentionError, setAttentionError] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  async function loadDashboard() {
    setError('')
    setAttentionError('')
    try {
      const [sessionResponse, dashboardResponse, supportResponse, emailAttentionResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/dashboard', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/support/notifications', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/email-notifications/attention-summary', { credentials: 'same-origin', cache: 'no-store' }),
      ])

      if (
        sessionResponse.status === 401
        || dashboardResponse.status === 401
        || supportResponse.status === 401
        || emailAttentionResponse.status === 401
      ) {
        window.location.replace('/admin/login?next=/admin')
        return
      }
      if (!sessionResponse.ok || !dashboardResponse.ok) throw new Error('dashboard unavailable')

      const sourceIssues: string[] = []
      setSession(await sessionResponse.json() as SessionPayload)
      setDashboard(await dashboardResponse.json() as DashboardPayload)

      if (supportResponse.ok) {
        setSupportNotifications(await supportResponse.json() as SupportNotificationsPayload)
      } else {
        setSupportNotifications(null)
        sourceIssues.push('Support')
      }

      if (emailAttentionResponse.ok) {
        setEmailAttention(await emailAttentionResponse.json() as EmailAttentionPayload)
      } else {
        setEmailAttention(null)
        sourceIssues.push('Email')
      }

      if (sourceIssues.length > 0) {
        setAttentionError(`${sourceIssues.join(' and ')} attention checks could not be loaded. Other Dashboard data is still current.`)
      }
    } catch {
      setError('Dashboard data could not be loaded. Please try again.')
    }
  }

  useEffect(() => {
    void loadDashboard()
  }, [])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    setError('')
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error('logout failed')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed. Please try again.')
      setLogoutBusy(false)
    }
  }

  if (error && (!session || !dashboard)) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel
          state="error"
          title="Dashboard unavailable"
          description={error}
          action={<AdminButton type="button" onClick={() => void loadDashboard()}>Retry</AdminButton>}
        />
      </main>
    )
  }

  if (!session || !dashboard) {
    return (
      <main className="admin-shell-gate-loading" aria-label="Loading Boss Zone dashboard">
        <AdminCard>
          <AdminSkeleton lines={6} />
        </AdminCard>
      </main>
    )
  }

  const { metrics, store } = dashboard
  const paidRate = metrics.ordersThisDrop > 0
    ? Math.round((metrics.paidOrdersThisDrop / metrics.ordersThisDrop) * 100)
    : 0
  const expiredPayments = dashboard.attention.payments.filter((item) => item.expired)
  const pendingPayments = dashboard.attention.payments.filter((item) => !item.expired)
  const unreadSupportReferences = Array.from(new Set(
    (supportNotifications?.notifications ?? []).map((item) => item.reference),
  ))
  const supportPreviewReferences = unreadSupportReferences.slice(0, 2)
  const emailFailureCount = emailAttention?.failedTotal ?? 0
  const fulfilmentAttention = dashboard.attention.fulfilment
  const hasAttention = expiredPayments.length > 0
    || emailFailureCount > 0
    || unreadSupportReferences.length > 0
    || fulfilmentAttention.count > 0
    || pendingPayments.length > 0
    || dashboard.attention.zeroStockAddOns.length > 0

  return (
    <AdminShell
      title="Dashboard"
      eyebrow="OVERVIEW"
      description={`${formatDropRange(dashboard.drop.startsAt, dashboard.drop.endsAt)} · Live operational snapshot from the authoritative order store.`}
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton variant="secondary" type="button" onClick={() => void loadDashboard()}>Refresh data</AdminButton>}
    >
      {error && <div className="admin-dashboard__inline-error" role="alert">{error}</div>}

      <section className="admin-dashboard__metric-grid" aria-label="Current drop summary">
        <AdminCard className="admin-dashboard__metric-card">
          <span className="admin-dashboard__metric-label">Orders this drop</span>
          <strong className="admin-dashboard__metric-value">{metrics.ordersThisDrop}</strong>
          <span className="admin-dashboard__metric-note">{metrics.paidOrdersThisDrop} paid · {paidRate}% paid</span>
        </AdminCard>
        <AdminCard className="admin-dashboard__metric-card">
          <span className="admin-dashboard__metric-label">Paid revenue</span>
          <strong className="admin-dashboard__metric-value">{formatMoney(metrics.paidRevenueThisDropCents)}</strong>
          <span className="admin-dashboard__metric-note">Confirmed paid orders in this drop</span>
        </AdminCard>
        <AdminCard className="admin-dashboard__metric-card">
          <span className="admin-dashboard__metric-label">Awaiting payment</span>
          <strong className="admin-dashboard__metric-value">{metrics.awaitingPaymentThisDrop}</strong>
          <span className={`admin-dashboard__metric-note ${metrics.awaitingPaymentThisDrop > 0 ? 'is-warning' : ''}`}>
            {metrics.awaitingPaymentThisDrop > 0 ? 'Needs payment follow-up' : 'Nothing waiting right now'}
          </span>
        </AdminCard>
        <AdminCard className="admin-dashboard__metric-card">
          <span className="admin-dashboard__metric-label">Customers this drop</span>
          <strong className="admin-dashboard__metric-value">{metrics.customersThisDrop}</strong>
          <span className="admin-dashboard__metric-note">Unique checkout email identities</span>
        </AdminCard>
      </section>

      <AdminCard className="admin-dashboard__attention-card">
        <div className="admin-dashboard__attention-header">
          <AdminCardHeader
            title="Needs Attention"
            description="Read-only operational exceptions from Orders, Support, Add-ons and Email & Notifications."
          />
        </div>

        {attentionError && (
          <div className="admin-dashboard__attention-source-warning" role="status">
            <AdminStatusPill tone="warning">Checks incomplete</AdminStatusPill>
            <span>{attentionError}</span>
          </div>
        )}

        {!hasAttention && !attentionError ? (
          <div className="admin-dashboard__attention-clear">
            <AdminStatusPill tone="success">All clear</AdminStatusPill>
            <div>
              <strong>Nothing needs attention</strong>
              <span>Payments, Support, fulfilment and operational checks are clear right now.</span>
            </div>
          </div>
        ) : (
          <div className="admin-dashboard__attention-list">
            {expiredPayments.map((item) => (
              <article className="admin-dashboard__attention-item" key={`expired-${item.orderId}`}>
                <div className="admin-dashboard__attention-copy">
                  <AdminStatusPill tone="danger">Payment window expired</AdminStatusPill>
                  <strong>{item.orderId}</strong>
                  <span>{item.customerName} · Bank transfer still unpaid</span>
                </div>
                <a className="admin-dashboard__attention-action" href={item.href}>Open order</a>
              </article>
            ))}

            {emailFailureCount > 0 && (
              <article className="admin-dashboard__attention-item">
                <div className="admin-dashboard__attention-copy">
                  <AdminStatusPill tone="danger">Email delivery issue</AdminStatusPill>
                  <strong>{emailFailureCount} failed email event{emailFailureCount === 1 ? '' : 's'}</strong>
                  <span>Transactional and internal notification failures requiring review.</span>
                </div>
                <a className="admin-dashboard__attention-action" href="/admin/email-notifications">Open Email</a>
              </article>
            )}

            {unreadSupportReferences.length > 0 && (
              <article className="admin-dashboard__attention-item">
                <div className="admin-dashboard__attention-copy">
                  <AdminStatusPill tone="warning">Unread Support</AdminStatusPill>
                  <strong>{unreadSupportReferences.length} unread Support ticket{unreadSupportReferences.length === 1 ? '' : 's'}</strong>
                  <span>{supportPreviewReferences.join(' · ')}</span>
                </div>
                <a className="admin-dashboard__attention-action" href="/admin/support">Open Support</a>
              </article>
            )}

            {fulfilmentAttention.count > 0 && (
              <article className="admin-dashboard__attention-item">
                <div className="admin-dashboard__attention-copy">
                  <AdminStatusPill tone="warning">Fulfilment</AdminStatusPill>
                  <strong>{fulfilmentAttention.count} order{fulfilmentAttention.count === 1 ? '' : 's'} still to fulfil</strong>
                  <div className="admin-dashboard__attention-order-links">
                    {fulfilmentAttention.orders.map((order) => (
                      <a href={order.href} key={order.orderId}>{order.orderId}</a>
                    ))}
                  </div>
                </div>
                <a className="admin-dashboard__attention-action" href={fulfilmentAttention.href}>Open Orders</a>
              </article>
            )}

            {pendingPayments.map((item) => (
              <article className="admin-dashboard__attention-item" key={`pending-${item.orderId}`}>
                <div className="admin-dashboard__attention-copy">
                  <AdminStatusPill tone="warning">Awaiting bank transfer</AdminStatusPill>
                  <strong>{item.orderId}</strong>
                  <span>{item.customerName} · Payment window still active</span>
                </div>
                <a className="admin-dashboard__attention-action" href={item.href}>Open order</a>
              </article>
            ))}

            {dashboard.attention.zeroStockAddOns.map((item) => (
              <article className="admin-dashboard__attention-item" key={`addon-${item.id}`}>
                <div className="admin-dashboard__attention-copy">
                  <AdminStatusPill tone="warning">Add-on stock exhausted</AdminStatusPill>
                  <strong>{item.name}</strong>
                  <span>No packs currently orderable</span>
                </div>
                <a className="admin-dashboard__attention-action" href={item.href}>Open Add-ons</a>
              </article>
            ))}
          </div>
        )}
      </AdminCard>

      <section className="admin-dashboard__operational-grid" aria-label="Operational status">
        <AdminCard>
          <AdminCardHeader title="Store status" description="Effective ordering state from the authoritative Store Status controls." />
          <div className="admin-dashboard__status-row">
            <AdminStatusPill tone={store.ordersOpen ? 'success' : 'neutral'}>{store.ordersOpen ? 'Orders open' : 'Orders closed'}</AdminStatusPill>
            {store.qaOverrideActive && <AdminStatusPill tone="warning">QA override active</AdminStatusPill>}
          </div>
          <dl className="admin-dashboard__definition-list">
            <div><dt>Weekly window</dt><dd>{store.schedule}</dd></div>
            <div><dt>Time zone</dt><dd>Pacific/Auckland</dd></div>
            <div><dt>State source</dt><dd>{storeStatusSourceLabel(store.stateSource)}</dd></div>
          </dl>
        </AdminCard>

        <AdminCard>
          <AdminCardHeader title="Weekend delivery" description="Current-week orders by selected weekday. Dates show current-week context." />
          <div className="admin-dashboard__delivery-split">
            <div>
              <span>{operationalWeekendLabels(dashboard.drop.startsAt).saturday}</span>
              <strong>{metrics.saturdayOrdersThisDrop}</strong>
              <small>orders</small>
            </div>
            <div>
              <span>{operationalWeekendLabels(dashboard.drop.startsAt).sunday}</span>
              <strong>{metrics.sundayOrdersThisDrop}</strong>
              <small>orders</small>
            </div>
          </div>
        </AdminCard>

        <AdminCard>
          <AdminCardHeader title="Business totals" description="Read-only lifetime totals from the current authoritative checkout order store." />
          <dl className="admin-dashboard__definition-list admin-dashboard__definition-list--totals">
            <div><dt>Orders recorded</dt><dd>{metrics.lifetimeOrders}</dd></div>
            <div><dt>Paid revenue recorded</dt><dd>{formatMoney(metrics.lifetimePaidRevenueCents)}</dd></div>
          </dl>
        </AdminCard>
      </section>

      <AdminCard padding="none" className="admin-dashboard__recent-card">
        <div className="admin-dashboard__section-header">
          <AdminCardHeader
            title="Recent orders"
            description="Latest orders from the authoritative D1 order store. Payment status reflects the stored order record."
          />
          <a className="admin-dashboard__text-link" href="/admin/orders">View Orders</a>
        </div>
        {dashboard.recentOrders.length > 0 ? (
          <AdminDataTable
            columns={RECENT_ORDER_COLUMNS}
            rows={dashboard.recentOrders}
            rowKey={(row) => `${row.orderId}-${row.createdAt}`}
            caption="Recent Sugar Gremlin orders"
          />
        ) : (
          <AdminStatePanel
            state="empty"
            title="No orders recorded yet"
            description="New website orders will appear here automatically after checkout records them."
          />
        )}
      </AdminCard>
    </AdminShell>
  )
}
